import moment from 'moment';
import { useContext, useEffect, useState } from 'react';
import COLORS from '../../common/data/enumColors';
import { getImageByModule } from '../../common/function/functionsUtils';
import AuthContext from '../../contexts/authContext';
import { searchActivities, updateActivity } from '../../odooApi/activites';
import Avatar from '../Avatar';
import ConfirmationPopup from '../PlanningCalendar/ConfirmationPopup';
import Badge from '../bootstrap/Badge';
import Button from '../bootstrap/Button';
import Card, { CardBody, CardTabItem } from '../bootstrap/Card';
import Dropdown, { DropdownItem, DropdownMenu, DropdownToggle } from '../bootstrap/Dropdown';
import OffCanvas, { OffCanvasBody, OffCanvasHeader, OffCanvasTitle } from '../bootstrap/OffCanvas';
import Spinner from '../bootstrap/Spinner';
import ErrorPopup from '../extras/ErrorPopup';
import ActivityCreatePopup from './ActivityCreatePopup';
import ActivityUpdatePopup from './ActivityUpdatePopup';
import { deleteAPI, postAPI, putAPI } from 'apis/backend/apiNode';

function TasksNotifications({ setOffcanvasStatus, lateTasks }) {
	const { userData } = useContext(AuthContext);

	const [todayTasks, setTodayTasks] = useState([]);
	const [otherNotDoneTasks, setOtherNotDoneTasks] = useState([]);
	const [completedTasks, setCompletedTasks] = useState([]);
	const [showAddModal, setShowAddModal] = useState(false);

	useEffect(() => {
		if (lateTasks) {
			const now = moment();
			const startOfToday = moment().startOf('day');
			const endOfToday = moment().endOf('day');

			// Tâches terminées (toutes)
			const completed = lateTasks.filter(task => task.state === 'completed');
			// Tâches non terminées
			const notDone = lateTasks.filter(task => task.state !== 'completed');

			// Parmi les non terminées : celles d'aujourd'hui
			const today = notDone.filter(task =>
				moment(task.datetime).isBetween(startOfToday, endOfToday, null, '[]')
			);
			// Les autres non terminées (avant ou après aujourd'hui)
			const otherNotDone = notDone.filter(task =>
				!moment(task.datetime).isBetween(startOfToday, endOfToday, null, '[]')
			);

			setTodayTasks(today);
			setOtherNotDoneTasks(otherNotDone);
			setCompletedTasks(completed);
		}
	}, [lateTasks]);

	return (
		<>
			<OffCanvas
				id='notificationCanvas'
				titleId='offcanvasExampleLabel'
				placement='end'
				isOpen={true}
				setOpen={() => { }}>
				<OffCanvasHeader setOpen={setOffcanvasStatus}>
					<OffCanvasTitle id='offcanvasExampleLabel'>Rappel</OffCanvasTitle>
				</OffCanvasHeader>
				<OffCanvasBody>
					<div className='row justify-content-end mb-3'>
						<Button
							className='col-auto'
							color='light'
							icon='AddTask'
							onClick={() => setShowAddModal(true)}>
							Créer
						</Button>
					</div>

					{/* SECTION 1 : Aujourd'hui */}
					{todayTasks.length > 0 && (
						<div className='mb-4'>
							<h6 className='fw-bold'>Aujourd'hui ({todayTasks.length})</h6>
							{todayTasks.map((task, i) => (
								<Task
									task={task}
									key={task._id || i}
									setTasks={setTodayTasks}
								/>
							))}
						</div>
					)}

					{/* SECTION 2 : Autres tâches non terminées */}
					{otherNotDoneTasks.length > 0 && (
						<div className='mb-4'>
							<h6 className='fw-bold'>Non terminées ({otherNotDoneTasks.length})</h6>
							{otherNotDoneTasks.map((task, i) => (
								<Task
									task={task}
									key={task._id || i}
									setTasks={setOtherNotDoneTasks}
								/>
							))}
						</div>
					)}

					{/* SECTION 3 : Tâches terminées */}
					{completedTasks.length > 0 && (
						<div className='mb-4'>
							<h6 className='fw-bold'>Terminées ({completedTasks.length})</h6>
							{completedTasks.map((task, i) => (
								<Task
									task={task}
									key={task._id || i}
									setTasks={setCompletedTasks}
								/>
							))}
						</div>
					)}
				</OffCanvasBody>
				{showAddModal && <AddRappelModal onClose={() => setShowAddModal(false)} />}
			</OffCanvas>
		</>
	);
}

const Task = ({ task, setTasks }) => {
	const isCompleted = task.state === 'completed';
	// Fonction pour supprimer
	const handleDelete = async () => {
		if (window.confirm("Supprimer ce rappel ?")) {
			try {
				await deleteAPI('rappel', `/${task._id}`);

				setTasks(prev => prev.filter(t => t._id !== task._id));
			} catch (error) {
				console.error("Erreur suppression", error);
			}
		}
	};

	// Fonction pour terminer ou Snooze
	const handleUpdate = async (newState, newTime = null) => {
		try {
			const payload = { state: newState };
			if (newTime) payload.datetime = newTime;

			await putAPI('rappel', payload, `/${task._id}`);
			// On retire de la vue locale une fois traité
			setTasks(prev => prev.filter(t => t._id !== task._id));
		} catch (error) {
			console.error("Erreur mise à jour", error);
		}
	};


	const borderColor = task.uiStatus || 'danger';
	const [editingRappel, setEditingRappel] = useState(null);
	return (
		<Card className={`mb-3 shadow-sm border-start border-${borderColor} border-4`}>
			<CardBody className='p-3'>
				<div className='d-flex justify-content-between align-items-start'>
					<div className='w-100'>
						<div className='d-flex justify-content-between'>
							<h6 className={`mb-1 ${isCompleted ? ' text-muted' : ''}`}>
								{task.note}
							</h6>
							{isCompleted && <Badge color='success' className='align-content-center' isLight>Terminé</Badge>}
							{task.uiStatus == 'danger' && <Badge color='danger' className='align-content-center' isLight>En retard</Badge>}
							{task.uiStatus == 'primary' && <Badge color='primary' className='align-content-center' isLight>Planifié</Badge>}
						</div>
						<span className='text-muted d-block'>
							<strong>Créé par :</strong> {task.createdBy?.name || "Système"}
						</span>
						<span className={isCompleted ? 'text-muted' : 'text-muted'}>
							<strong>Deadline :</strong> {
								moment.utc(task.datetime).local().isSame(moment(), 'day')
									? moment.utc(task.datetime).local().format('HH:mm')
									: moment.utc(task.datetime).local().format('DD/MM/YYYY HH:mm')
							}
						</span>
					</div>
				</div>

				{!isCompleted && (
					<>
						<hr className='my-2' />
						<div className='d-flex flex-wrap gap-1 justify-content-between'>
							<div className='btn-group'>
								<Button color='success' icon='Done'
									onClick={() => handleUpdate('completed')} title='Terminer' />
								<Button color='warning' icon='Snooze'
									onClick={() => handleUpdate('pending', moment().add(15, 'm').toISOString())} title='+15 min' />
							</div>
							<div className='btn-group'>
								<Button color='info' icon='Edit' title='Modifier' onClick={() => setEditingRappel(true)} />
								<Button color='danger' icon='Delete'
									onClick={handleDelete} title='Supprimer' />
							</div>
						</div>
					</>
				)}
			</CardBody>

			{editingRappel && (
				<EditRappelModal
					rappel={task}
					onClose={() => setEditingRappel(null)}
				/>
			)}
		</Card>
	);
};

const EditRappelModal = ({ rappel, onClose, onRefresh }) => {
	const { userData, socket } = useContext(AuthContext);

	// On vérifie si l'utilisateur actuel est le créateur
	const isCreator = rappel.createdBy?.id === userData.id;

	const [note, setNote] = useState(rappel.note);
	const [datetime, setDatetime] = useState(
		rappel.datetime ? moment(rappel.datetime).format("YYYY-MM-DDTHH:mm") : ""
	);
	const [loading, setLoading] = useState(false);

	const handleUpdate = async (status = 'pending') => {
		setLoading(true);
		try {
			const utcDatetime = moment(datetime).utc().format();
			const payload = {
				datetime: utcDatetime,
				state: status
			};

			// On n'envoie la note que si on est le créateur
			if (isCreator) {
				payload.note = note;
			}

			await putAPI('rappel', payload, `/${rappel._id}`);
			socket.emit("updateRappel");
			onClose();
		} catch (error) {
			console.error(error);
			alert("Action failed");
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
			<div className="modal-dialog ">
				<div className="modal-content border-0 shadow-lg">
					<div className="modal-header bg-light">
						<h5 className="modal-title">⚙️ Gérer le Rappel</h5>
						<button type="button" className="btn-close" onClick={onClose}></button>
					</div>

					<div className="modal-body">
						<div className="mb-3">
							<label className="form-label fw-bold">Note / Message</label>
							<textarea
								className="form-control"
								rows="3"
								value={note}
								onChange={e => setNote(e.target.value)}
								// DESACTIVE si pas créateur
								disabled={!isCreator}
								readOnly={!isCreator}
								style={!isCreator ? { backgroundColor: '#f8f9fa', cursor: 'not-allowed' } : {}}
							/>
							{!isCreator && (
								<small className="text-muted">Seul le créateur peut modifier le texte de la note.</small>
							)}
						</div>

						<div className="mb-3">
							<label className="form-label fw-bold">Date & Heure du Rappel</label>
							<input
								type="datetime-local"
								className="form-control"
								value={datetime}
								onChange={e => setDatetime(e.target.value)}
							/>
						</div>

						{rappel.state === 'completed' && (
							<div className="alert alert-info py-2">
								<small>Ce rappel est déjà marqué comme terminé.</small>
							</div>
						)}
					</div>

					<div className="modal-footer d-flex justify-content-between">
						<button type="button" className="btn btn-secondary" onClick={onClose}>
							Annuler
						</button>

						<div className="btn-group">
							{rappel.state === 'pending' && (
								<button
									type="button"
									className="btn btn-success"
									onClick={() => handleUpdate('completed')}
									disabled={loading}
								>
									✅ Terminer
								</button>
							)}

							<button
								type="button"
								className="btn btn-primary"
								onClick={() => handleUpdate('pending')}
								disabled={loading}
							>
								💾 Enregistrer
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};
const AddRappelModal = ({ targetUser, onClose, onRefresh }) => {
	const { userData, socket } = useContext(AuthContext)
	const [formData, setFormData] = useState({
		note: "",
		datetime: ""
	});
	const [isSubmitting, setIsSubmitting] = useState(false);

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (!formData.note || !formData.datetime) return alert("Please fill all fields");

		setIsSubmitting(true);
		try {
			// Adjust the URL to your actual API path
			const utcDatetime = moment(formData.datetime).utc().format();
			await postAPI("rappel", {
				target: userData.id,
				note: formData.note,
				datetime: utcDatetime,
				state: "pending"
			})
			socket.emit("updateRappel");

			if (onRefresh) onRefresh(); // Callback to refresh the list in parent component
			onClose();
		} catch (error) {
			console.error("Error creating rappel:", error);
			alert("Failed to create rappel: " + (error.response?.data?.error || error.message));
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
			<div className="modal-dialog ">
				<div className="modal-content shadow">
					<div className="modal-header bg-light">
						<h5 className="modal-title">⏰ Add Reminder for {targetUser?.userName}</h5>
						<button type="button" className="btn-close" onClick={onClose}></button>
					</div>
					<form onSubmit={handleSubmit}>
						<div className="modal-body">
							<div className="mb-3">
								<label className="form-label">Reminder Note</label>
								<textarea
									className="form-control"
									rows="3"
									placeholder="What should we remind about?"
									value={formData.note}
									onChange={(e) => setFormData({ ...formData, note: e.target.value })}
									required
								></textarea>
							</div>
							<div className="mb-3">
								<label className="form-label">Reminder Date & Time</label>
								<input
									type="datetime-local"
									className="form-control"
									value={formData.datetime}
									onChange={(e) => setFormData({ ...formData, datetime: e.target.value })}
									required
								/>
							</div>
						</div>
						<div className="modal-footer">
							<button type="button" className="btn btn-secondary" onClick={onClose} disabled={isSubmitting}>
								Cancel
							</button>
							<button type="submit" className="btn btn-warning" disabled={isSubmitting}>
								{isSubmitting ? "Creating..." : "Set Reminder"}
							</button>
						</div>
					</form>
				</div>
			</div>
		</div>
	);
};

export default TasksNotifications;


